import { JSX, useState } from "react";
import MainLayout from "../components/layout/MainLayout";
import MainLayoutUser from "../components/layout/MainLayoutUser";
import MainLayoutAdmin from "../components/layout/MainLayoutAdmin";
import BannerStatic from "../components/organisms/BannerStatic";
import JobListingGrid from "../components/organisms/JobListingGrid";
import BannerJobSearch from "../components/organisms/BannerJobSearch";
import { useEffect } from "react";
import { useAuthStore } from "../store/useAuthStore";

interface PolicySection {
    title: string;
    content: JSX.Element;
}


export default function PrivacyPolicy() {


    useEffect(() => {
        document.title = "TalentLink | Privacy Policy";

        // Meta description
        let metaDescription = document.querySelector("meta[name='description']");
        if (metaDescription) {
            metaDescription.setAttribute("content", "Halaman utama TalentLink");
        }

        // Meta keywords
        let metaKeywords = document.querySelector("meta[name='keywords']");
        if (!metaKeywords) {
            metaKeywords = document.createElement("meta");
            metaKeywords.setAttribute("name", "keywords");
            document.head.appendChild(metaKeywords);
        }

        metaKeywords.setAttribute(
            "content",
            "lowongan kerja, job portal, karir, rekrutmen, TalentLink, cari kerja, hiring"
        );
    }, []);

    const sections: PolicySection[] = [
        {
            title: "Introduction",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>
                        <strong>Talentlinkindo</strong> (“We”, “Us”, or “Our”) operating through{" "}
                        <a
                            href="https://talentlinkindo.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                        >
                            talentlinkindo.com
                        </a>
                        , is an entity engaged in providing job vacancy information to job seekers and supplying
                        job seeker data and competency profiles to employers. These activities are conducted
                        online through our Job Portal and supported by recruitment consulting and headhunting
                        services.
                    </p>
                    <p>
                        We recognize the importance of protecting personal data belonging to job seekers and
                        employers/companies (“You”) and are committed to safeguarding the privacy and
                        confidentiality of all information collected through our platform.
                    </p>
                    <p>
                        This Privacy Policy (“Policy”) outlines the principles and procedures governing the
                        collection, use, disclosure, and protection of your personal data, in accordance with
                        applicable laws and regulations of the Republic of Indonesia, including but not limited
                        to <strong>Law No. 11 of 2008 on Electronic Information and Transactions (ITE Law)</strong>{" "}
                        and <strong>Law No. 27 of 2022 on Personal Data Protection (PDP Law)</strong>.
                    </p>
                    <p>
                        By accessing or using our website and related services, you acknowledge that you have
                        read, understood, and agreed to be bound by the terms of this Policy. Talentlinkindo
                        reserves the right to modify, amend, or update this Policy from time to time without
                        prior notice.
                    </p>
                </div>
            ),
        }, {
            title: "Definition",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>For the purposes of this Policy:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            <strong>“Client”</strong> means any company or organization that advertises, offers, or promotes job
                            opportunities and/or employment-related activities on our platform.
                        </li>
                        <li>
                            <strong>“Candidate”</strong> means an individual who registers, opens an account, and accesses the Platform's
                            services for the purpose of seeking employment.
                        </li>
                        <li>
                            <strong>“Platform”</strong> refers to all websites, mobile applications, and other online media operated by Talentlinkindo, including but not limited to versions intended for both job seekers and
                            employers.
                        </li>
                        <li>
                            <strong>“Personal Data”</strong> means any information relating to an identified or identifiable individual as defined by applicable data protection legislation.
                        </li>
                        <li>
                            <strong>“You”</strong> or <strong>“Your”</strong>  refers to Clients or Candidates accessing or using the Platform.
                        </li>
                        <li>
                            <strong>“Talentlinkindo”, </strong><strong>“We”, </strong><strong>“Us”, </strong>or <strong>“Our”</strong> refers collectively to{" "}
                            <a
                                href="https://talentlinkindo.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                            >
                                talentlinkindo.com
                            </a>
                            {" "}and its affiliates responsible for the operation, management, and maintenance of the
                            Platform.
                        </li>
                    </ul>
                </div>
            ),

        }, {
            title: "Collection and Use of Personal Data",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p><strong>3.1 Candidates</strong></p>
                    <p>We collect personal data from Candidates upon registration and participation on our Platform.
                        The information collected may include, but is not limited to:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            Full name;
                        </li>
                        <li>
                            Photograph;
                        </li>
                        <li>
                            Date of birth and nationality;
                        </li>
                        <li>
                            Gender;
                        </li>
                        <li>
                            Contact information (email address, telephone number, domicile address);
                        </li>
                        <li>
                            Employment preferences and work readiness;
                        </li>
                        <li>
                            Marital status;
                        </li>
                        <li>
                            Educational background;
                        </li>
                        <li>
                            Curriculum Vitae (CV)/Resume;
                        </li>
                        <li>
                            Language proficiency and technical skills;
                        </li>
                        <li>
                            Current and expected remuneration details; and
                        </li>
                        <li>
                            Any additional information deemed necessary to facilitate accurate and suitable candidate placement.
                        </li>
                    </ul>
                    <p>Candidate data may be disclosed to Clients in accordance with applicable laws and contractual
                        safeguards to ensure compliance with this Policy and relevant legal standards.</p>
                    <p><strong>3.2 Client Data</strong></p>
                    <p>We collect and process company-related information when a Client registers or utilizes our
                        Platform, which may include:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            Company name and logo;
                        </li>
                        <li>
                            Contact details (email address, telephone number, business address, website URL);
                        </li>
                        <li>
                            Number of employees;
                        </li>
                        <li>
                            Nature of business activities;
                        </li>
                        <li>
                            Legal documentation;
                        </li>
                        <li>
                            Job openings and role descriptions;
                        </li>
                        <li>
                            Corporate profile summary; and
                        </li>
                        <li>
                            Any other data reasonably required for operational and service provision purposes.
                        </li>
                    </ul>

                    <p><strong>3.3 Purposes of Processing</strong></p>
                    <p>Talentlinkindo may process and utilize personal data for the following legitimate business
                        purposes:</p>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            To provide recruitment, job-matching, and related employment services to Candidates and Clients;
                        </li>
                        <li>
                            To communicate with Candidates and Clients in relation to recruitment processes;
                        </li>
                        <li>
                            To enhance, develop, and market Talentlinkindo’s services and promotional initiatives;
                        </li>
                        <li>
                            To facilitate data usage for service provision, event participation, or marketing purposes;
                        </li>
                        <li>
                            To analyze Platform usage and improve operational efficiency;
                        </li>
                        <li>
                            To comply with statutory and regulatory obligations; and
                        </li>
                        <li>
                            To achieve any other lawful purpose consistent with Talentlinkindo’s business operations.
                        </li>
                    </ul>

                    <p><strong>3.4 Consent</strong></p>
                    <p>By accessing, registering, or using our platform, you acknowledge and voluntarily consent to provide your personal data for the above purposes.</p>

                    <p><strong>3.5 Rights and Licensing</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                            <strong>Clients</strong> grant Talentlinkindo the right to reproduce, distribute, and display their logos and names for promotional purposes.
                        </li>
                        <li>
                            <strong>Candidates</strong> grant Talentlinkindo permission to use, reproduce, and share submitted data as part of recruitment activities.
                        </li>
                    </ul>

                    <p><strong>3.6 Sensitive Data</strong></p>
                    <p>We will not request or process sensitive personal data unless you provide written consent or it is required by law.</p>
                </div>
            ),

        }, {
            title: "Sharing and Disclosure",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p><strong>4.1 Data Sharing</strong></p>
                    <p>Candidate data (resume, employment history, education, contacts, salary expectations, etc.)
                        may be shared with Talentlinkindo employees, affiliates, and relevant Clients to enhance job
                        opportunities.</p>
                    <p>Clients may access candidate data to contact potential hires. By using our platform, you consent
                        to possible international data transfers.</p>
                    <p>Talentlinkindo may deactivate or delete profiles at any time and is not liable for any misuse of
                        data by Clients.</p>
                    <p><strong>4.2 Legal Disclosure</strong></p>
                    <p>We may disclose your data to law enforcement, regulators, or government bodies when required
                        by law. Data may also be transferred to new business owners during reorganizations, mergers,
                        or acquisitions.</p>
                </div>
            ),

        }, {
            title: "Data Security",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p><strong>5.1 Security Measures</strong></p>
                    <p>We implement appropriate technical and organizational measures to safeguard your personal
                        data from unauthorized access, loss, or misuse. Only authorized personnel with a legitimate
                        need-to-know basis have access to this data.
                    </p>
                    <p><strong>5.2 User Responsibility</strong></p>
                    <p>You are responsible for securing your account credentials and promptly notifying us of
                        unauthorized access. You assume full responsibility for any activity on your account resulting
                        from your actions.
                    </p>
                    <p><strong>5.3 Limitations</strong></p>
                    <p>While we maintain high standards for data protection, no system is 100% secure. We cannot
                        guarantee absolute security but commit to continual protection efforts.
                    </p>

                    <p><strong>5.4 Data Accuracy</strong></p>
                    <p>You must provide accurate and truthful information. Any falsification or misinformation is your
                        responsibility, and Talentlinkindo shall not be liable for any related claims.
                    </p>


                </div>
            ),

        }, {
            title: "Data Retention",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>We retain your personal data for as long as necessary to fulfill collection purposes or legal requirements.
                    </p>
                    <p>You may terminate your account by contacting us (see Section 9). However, certain data may be retained to comply with laws, resolve disputes, prevent fraud, or for other legitimate reasons. Data no longer required will be securely deleted.
                    </p>


                </div>
            ),

        }, {
            title: "Your Rights",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>You have the right to access, correct, or delete your personal data via your profile or by
                        contacting us through email (see Section 9).
                    </p>
                    <p>Your request may be limited by legal obligations or legitimate interests that require us to retain specific data.
                    </p>


                </div>
            ),

        }, {
            title: "Payments",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>If a Client purchases paid services, they agree to applicable fees, taxes, and related terms.
                    </p>
                    <p>
                        Limited payment details may be collected for processing.
                    </p>
                    <p>
                        Service prices may be adjusted with prior notice, and exchange rate differences may apply.
                    </p>


                </div>
            ),

        }, {
            title: "Limitations of Liability",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>Talentlinkindo is not responsible for information exchanged outside the Platform (via email, phone, SMS, etc.).
                    </p>
                    <p>
                        By using our services, you agree not to file any claim or legal action against Talentlinkindo for matters arising from such external interactions.
                    </p>
                </div>
            ),

        }, {
            title: "Contact Information",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>For questions or concerns, please contact us via email at {" "}
                        <a
                            href="mailto:info@talentlinkindo.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                        >
                            info@talentlinkindo.com.
                        </a>
                    </p>
                </div>
            ),

        }, {
            title: "Policy Changes",
            content: (
                <div className="space-y-4 text-gray-700">
                    <p>We may update this Policy periodically without prior notice. Continued use of our platform
                        indicates your acceptance of any changes made.
                    </p>
                </div>
            ),

        },
        // section lain bisa ditambahkan sama caranya
    ];

    const [activeIndex, setActiveIndex] = useState(0);

    const { accessToken, type } = useAuthStore();
    useEffect(() => {
        document.title = "TalentLink | Privacy Policy";

        const metaDescription = document.querySelector("meta[name='description']");
        if (metaDescription) {
            metaDescription.setAttribute("content", "Halaman utama TalentLink");
        }
    }, []);

    const Layout =
        !accessToken
            ? MainLayout
            : type === "candidate"
                ? MainLayoutUser
                : type === "company"
                    ? MainLayoutAdmin
                    : MainLayout;

    return (
        <Layout>

            <BannerStatic blur>
                <h2 className="text-3xl md:text-5xl font-medium mb-4 md:w-3/4 lg:w-2/3 text-shadow">
                    Privacy Policy
                </h2>
                <p className="text-base md:text-xl font-light mb-8 w-full md:w-3/4 lg:w-2/3 text-shadow">
                    Talentlinkindo takes your privacy very seriously!
                </p>
            </BannerStatic>
            <div className="container mx-auto px-6 pt-6 flex gap-8 flex-col-reverse md:flex-row">

                {/* Sidebar kiri */}
                <div className="md:w-1/4 mb-6 md:mb-0">
                    <ul className="space-y-2 border rounded-lg p-4 bg-white shadow">
                        {sections.map((section, idx) => (
                            <li
                                key={idx}
                                onClick={() => setActiveIndex(idx)}
                                className={`cursor-pointer p-2 rounded-lg ${activeIndex === idx ? "bg-blue-100 text-blue-600 font-semibold" : "text-gray-800 hover:bg-gray-100"
                                    }`}
                            >
                                {section.title}
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Konten kanan */}
                <div className="md:w-3/4 bg-white p-6 rounded shadow border h-[575px] overflow-y-auto">
                    <h2 className="text-xl font-semibold mb-4">{sections[activeIndex].title}</h2>
                    <div>{sections[activeIndex].content}</div>
                </div>
            </div>

        </Layout>
    );
}
